import { SaveStatus } from "../saveSlot";
import { START } from "./spine";
import { TelemetryClient, TelemetryTransport } from "./TelemetryClient";

/**
 * The demo's telemetry, as the shell uses it: one client for the page,
 * installed once at boot (`installTelemetry`) and reached from anywhere
 * through `track`, `count`, `beginView`, and `sendFeedback`. Before
 * installation, or in a build that sends nothing, every call is a no-op
 * or a console line, so no caller has to ask whether telemetry is on.
 *
 * What it answers, and how (issue #248): where demo players stop, how
 * long each step took them, and what they did in between. The
 * milestones — every story scene as it plays, every tutorial step as
 * it is satisfied — are recorded by the TelemetryRecorder entity, which
 * listens to the world's events and never dispatches one; it also
 * keeps the client's spine position current and its play clock paused
 * with the game. Where the player went (the store, a scavenging trip,
 * the bench, the manual, the journal, the pause menu) is recorded as
 * a `view` with its length, by the recorder for places in the world
 * and by the chrome for places in the DOM. The stuck signals (Escape
 * pressed, a key that did nothing at a machine) are counted at the
 * dispatch points that see them. Errors come off `window`'s error and
 * unhandledrejection listeners, which nothing else installs. The
 * session's start and end frame all of it.
 *
 * Anonymous by construction: a random player id in localStorage so a
 * return visit is recognized, a random session id per page load, no
 * cookies, no fingerprinting, and the function that receives the batch
 * stores no address. The start menu says as much to the player.
 *
 * Where it goes is a build-time define (`process.env.TELEMETRY_ENDPOINT`,
 * esbuild-client.config.mjs): production and dev builds both post to
 * /api/telemetry — the Vercel function, or the dev server's copy of it
 * writing to a local database — and the E2E build gets an empty string
 * and logs each batch to the console instead.
 */

const PLAYER_KEY = "woodworking-tycoon-player";

const ENDPOINT = process.env.TELEMETRY_ENDPOINT ?? "";

/** Whether this build reports anywhere — the start menu's sentence keys on it. */
export function telemetryEnabled(): boolean {
  return ENDPOINT !== "";
}

let client: TelemetryClient | null = null;

/** The installed client, for callers that need its ids. Null before boot. */
export function telemetryClient(): TelemetryClient | null {
  return client;
}

export function track(name: string, props?: Record<string, unknown>): void {
  client?.track(name, props);
}

export function count(key: string): void {
  client?.count(key);
}

/** The player went somewhere; call the closer when they leave. */
export function beginView(
  id: string,
  props?: Record<string, unknown>,
): () => void {
  return client?.beginView(id, props) ?? (() => {});
}

/** The player chose New Game: a playthrough begins at the spine's start. */
export function startPlaythrough(): void {
  client?.track("newGame");
  client?.moveTo(START);
}

/**
 * Send the player's note with their save. Resolves to whether it
 * arrived; a build that sends nothing resolves true after logging it.
 */
export function sendFeedback(
  note: string,
  save: unknown | null,
): Promise<boolean> {
  return client ? client.sendFeedback(note, save) : Promise.resolve(false);
}

/**
 * Stand the page's client up, announce the session, and hook the page's
 * lifecycle: `pagehide` ends the session, the tab going hidden stops
 * the play clock and flushes what's queued (a phone that evicts a
 * background tab never fires `pagehide`), and the error listeners
 * record what nothing else catches.
 */
export function installTelemetry(options: {
  build: string;
  hadSave: SaveStatus;
}): TelemetryClient {
  client = new TelemetryClient({
    transport: ENDPOINT ? beaconTransport(ENDPOINT) : consoleTransport(),
    playerId: playerId(),
    sessionId: randomId(),
    build: options.build,
    hidden: document.visibilityState === "hidden",
  });
  client.track("session_start", {
    hadSave: options.hadSave,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    touch: navigator.maxTouchPoints > 0,
  });

  window.addEventListener("pagehide", () => client?.end());
  document.addEventListener("visibilitychange", () => {
    const hidden = document.visibilityState === "hidden";
    client?.setHidden(hidden);
    if (hidden) client?.flush();
  });
  window.addEventListener("error", (event) => {
    client?.track("error", {
      message: clip(event.message),
      source: sourceOf(event.filename, event.lineno, event.colno),
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason ?? "");
    client?.track("error", { message: clip(message), source: "promise" });
  });
  return client;
}

/** The stable id this browser plays under; minted on first sight. */
function playerId(): string {
  try {
    const stored = localStorage.getItem(PLAYER_KEY);
    if (stored) return stored;
    const minted = randomId();
    localStorage.setItem(PLAYER_KEY, minted);
    return minted;
  } catch {
    // Storage off (private mode, quota): the id lives for this page only.
    return randomId();
  }
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Array.from({ length: 4 }, () =>
    Math.floor(Math.random() * 0xffffffff)
      .toString(16)
      .padStart(8, "0"),
  ).join("-");
}

function clip(text: string): string {
  return text.length > 500 ? `${text.slice(0, 500)}…` : text;
}

/** The throwing file as a path on this origin, with its line and column. */
function sourceOf(
  filename: string | undefined,
  line: number | undefined,
  column: number | undefined,
): string {
  if (!filename) return "";
  const path = filename.startsWith(window.location.origin)
    ? filename.slice(window.location.origin.length)
    : filename;
  return line ? `${path}:${line}:${column ?? 0}` : path;
}

function beaconTransport(endpoint: string): TelemetryTransport {
  return {
    beacon(body) {
      // A Blob typed as JSON so the function can parse the body as-is.
      const blob = new Blob([body], { type: "application/json" });
      try {
        return navigator.sendBeacon(endpoint, blob);
      } catch {
        return false;
      }
    },
    async post(body) {
      try {
        const response = await fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body,
          keepalive: true,
        });
        return response.ok;
      } catch {
        return false;
      }
    },
  };
}

/** Dev and E2E builds: every batch to the console, nothing on the wire. */
function consoleTransport(): TelemetryTransport {
  return {
    beacon(body) {
      console.info("[telemetry]", JSON.parse(body));
      return true;
    },
    async post(body) {
      console.info("[telemetry]", JSON.parse(body));
      return true;
    },
  };
}
